import { useMemo, useState } from "react";
import { useUserContext } from "../../../../../contexts/UserContext";
import { useRefetchRowDataList } from "../../../../../hooks";
import {
  RowData,
  RowDataReducerAction,
  RowDataReducerState,
  SaveRecordRequest,
} from "../../../../../types";
import {
  exportRecords,
  getCompanyIdByStoreId,
  getTranslatedColDefs,
  handleFileTemplateDownload,
  parseExcelFile,
  processBulkUpload,
  resetFileUploadData,
  updateFileUploadData,
  validateBulkUploadRecords,
} from "../../../helpers";
import {
  getImportStatusCounts,
  getImportToastMessage,
} from "../../../helpers/importRecordsHelpers";
import { useFileUpload } from "../../table-actions/FileUpload/useFileUpload";
import FileUploadDialog from "../../table-actions/FileUpload/FileUploadDialog";
import { DEFAULT_TOAST, TOAST_MESSAGES } from "../../../constants";
import { t } from "i18next";
import { UploadState } from "../../table-actions/FileUpload/constants";

interface CoaFileUploadProps {
  isOpen: boolean;
  onClose: () => void;
  rowDataReducerState: RowDataReducerState;
  dispatch: React.Dispatch<RowDataReducerAction>;
}

export default function CoaFileUpload({
  isOpen,
  onClose,
  rowDataReducerState,
  dispatch,
}: Readonly<CoaFileUploadProps>): JSX.Element {
  const { schema, columnDefs, fileRowDataList = [] } = rowDataReducerState;
  const { selected, setupBaseRequestInfo, iamUserState } = useUserContext();
  const setupName = selected.setup.label;
  const metaData = {
    store_id: setupBaseRequestInfo.storeId,
    company_id: getCompanyIdByStoreId(
      selected.store.value,
      iamUserState.extendedAccountInfo,
    ),
    enterprise_id: setupBaseRequestInfo.enterpriseId,
  };
  const [shouldRefetch, setShouldRefetch] = useState(false);

  const { refetch: refetchRowDataList } = useRefetchRowDataList({
    setupBaseRequestInfo,
    headerLabel: undefined,
    dispatch,
    setTableTitleInfo: undefined,
  });

  const handleParseFile = async (file: File) => {
    return parseExcelFile(file, schema, metaData, setupName);
  };

  const handleValidateFile = async (data: RowData[]) => {
    const request: SaveRecordRequest = {
      ...setupBaseRequestInfo,
      record: data,
    };
    return validateBulkUploadRecords(request);
  };

  const {
    uploadState,
    validatedData,
    setUploadState,
    attachedFile,
    toastMessage,
    hasErrors,
    handleFileChange,
    removeFile,
    handleValidate,
    resetUpload,
    setToastMessage,
  } = useFileUpload({
    parseFile: handleParseFile,
    validateFile: handleValidateFile,
  });

  const handleImport = async () => {
    setToastMessage(DEFAULT_TOAST);
    setUploadState(UploadState.IMPORTING);
    try {
      const request: SaveRecordRequest = {
        ...setupBaseRequestInfo,
        record: validatedData,
      };
      const result = await processBulkUpload(request);

      const counts = getImportStatusCounts(result.response);
      setToastMessage(getImportToastMessage(counts));
      setShouldRefetch(result.shouldRefetchData);
      updateFileUploadData(dispatch, result.response);
    } catch {
      setToastMessage(TOAST_MESSAGES.FILE_PROCESSING_ERROR());
    } finally {
      setUploadState(UploadState.IMPORT_COMPLETE);
    }
  };

  const handleClose = async () => {
    if (shouldRefetch) {
      await refetchRowDataList();
    }
    resetUpload();
    onClose();
  };

  const languageSpecificColDefs = useMemo(
    () => columnDefs ?? getTranslatedColDefs(schema, t),
    [schema, columnDefs],
  );

  const handleDownloadErrorReport = () => {
    const fileName = `${setupName}-Error-Report`
      .replace(/\s*-\s*/g, "-")
      .replace(/\s+/g, "-");
    exportRecords({
      rowDataList: validatedData,
      fileName: fileName,
      format: "excel",
      rowDataReducerState,
      selected,
      iamUserState,
    });
  };

  return (
    <FileUploadDialog
      isOpen={isOpen}
      title="Upload File"
      uploadState={uploadState}
      attachedFile={attachedFile}
      toastMessage={toastMessage}
      columnDefs={languageSpecificColDefs}
      tableData={
        UploadState.VALIDATION_COMPLETE === uploadState
          ? validatedData
          : fileRowDataList
      }
      hasErrors={hasErrors}
      onClose={handleClose}
      onFileChange={handleFileChange}
      onFileRemove={removeFile}
      onValidate={handleValidate}
      onImport={handleImport}
      onReset={() => {
        resetUpload();
        resetFileUploadData(dispatch);
      }}
      onDownloadTemplate={() =>
        void handleFileTemplateDownload(schema, setupName)
      }
      onDownloadErrorReport={handleDownloadErrorReport}
      onClearToast={() => setToastMessage(DEFAULT_TOAST)}
    />
  );
}
